import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FileText, Plus, Save, Download, Sparkles, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { streamSSE, API_BASE } from "@/lib/api";

const STATUS_OPTIONS = ["draft", "approved", "final"];

export default function Documents() {
    const [docs, setDocs] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [active, setActive] = useState(null);
    const [content, setContent] = useState("");
    const [title, setTitle] = useState("");
    const [status, setStatus] = useState("draft");
    const [saving, setSaving] = useState(false);
    const [versions, setVersions] = useState([]);
    const [showTemplate, setShowTemplate] = useState(false);
    const [newTitle, setNewTitle] = useState("");
    const [newTemplate, setNewTemplate] = useState("blank");
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiStreaming, setAiStreaming] = useState(false);

    const loadList = useCallback(async () => {
        const { data } = await api.get("/documents");
        setDocs(data.documents || []);
    }, []);

    const loadDoc = useCallback(async (id) => {
        const { data } = await api.get(`/documents/${id}`);
        setActive(data.document);
        setVersions(data.versions || []);
        setTitle(data.document.title);
        setContent(data.document.content || "");
        setStatus(data.document.status);
    }, []);

    useEffect(() => {
        loadList();
        api.get("/document-templates").then((r) => setTemplates(r.data.templates || []));
    }, [loadList]);

    useEffect(() => {
        if (activeId) loadDoc(activeId);
    }, [activeId, loadDoc]);

    const create = async () => {
        if (!newTitle.trim()) return toast.error("Add a title");
        const { data } = await api.post("/documents", { title: newTitle, type: newTemplate, template: newTemplate });
        setShowTemplate(false);
        setNewTitle("");
        setNewTemplate("blank");
        await loadList();
        setActiveId(data.id);
    };

    const save = async () => {
        if (!active) return;
        setSaving(true);
        try {
            await api.patch(`/documents/${active.id}`, { title, content, status });
            await loadList();
            await loadDoc(active.id);
            toast.success("Saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        await api.delete(`/documents/${id}`);
        if (activeId === id) { setActiveId(null); setActive(null); }
        await loadList();
    };

    const exportPdf = async () => {
        const token = localStorage.getItem("wfos_token");
        const res = await fetch(`${API_BASE}/documents/${active.id}/export-pdf`, {
            method: "POST", headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return toast.error("PDF export failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${active.title || "document"}.pdf`; a.click();
        URL.revokeObjectURL(url);
    };

    const aiDraft = async () => {
        if (!active) return;
        if (!aiPrompt.trim()) return toast.error("Describe what you want drafted");
        setAiStreaming(true);
        let final = "";
        try {
            await streamSSE(`/documents/${active.id}/ai-draft`, { prompt: aiPrompt }, {
                onDelta: (_, all) => { final = all; setContent(all); },
                onDone: async (full) => {
                    setContent(full);
                    setAiPrompt("");
                    // Auto-save the draft
                    await api.patch(`/documents/${active.id}`, { content: full });
                    await loadDoc(active.id);
                    toast.success("Draft ready");
                },
            });
        } catch (e) {
            toast.error(String(e.message || e));
        } finally {
            setAiStreaming(false);
        }
    };

    const grouped = useMemo(() => {
        const g = {};
        for (const d of docs) {
            const t = d.type || "blank";
            (g[t] = g[t] || []).push(d);
        }
        return g;
    }, [docs]);

    return (
        <div className="h-full flex bg-white">
            {/* LEFT rail */}
            <aside className="w-[260px] flex-shrink-0 border-r border-[#e8e8e3] bg-[#fafaf7] flex flex-col overflow-y-auto wfos-scrollbar">
                <div className="p-4">
                    <div className="wfos-label mb-2">Documents</div>
                    <button
                        onClick={() => setShowTemplate(true)}
                        data-testid="doc-new-btn"
                        className="w-full inline-flex items-center justify-center gap-1 rounded-lg bg-[#16a34a] px-3 h-9 text-xs font-medium text-white hover:bg-[#15803d]"
                    >
                        <Plus className="w-3.5 h-3.5" strokeWidth={2} /> New document
                    </button>
                </div>
                <div className="px-3 pb-6 space-y-3">
                    {Object.keys(grouped).sort().map((type) => (
                        <div key={type}>
                            <div className="wfos-label px-1 mb-1">{type}</div>
                            <ul className="space-y-0.5">
                                {grouped[type].map((d) => (
                                    <li key={d.id}>
                                        <button
                                            onClick={() => setActiveId(d.id)}
                                            data-testid={`doc-list-${d.id}`}
                                            className={`w-full text-left rounded-md px-2 h-9 flex items-center gap-2 text-sm hover:bg-[#f4f4ef] ${activeId === d.id ? "bg-white border border-[#e8e8e3]" : ""}`}
                                        >
                                            <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" strokeWidth={1.75} />
                                            <span className="truncate">{d.title}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {docs.length === 0 && (
                        <div className="text-xs text-gray-400 px-1">No documents yet. Click "New document".</div>
                    )}
                </div>
            </aside>

            {/* CENTER */}
            <main className="flex-1 flex flex-col min-w-0 bg-white">
                {active ? (
                    <>
                        <div className="border-b border-[#e8e8e3] px-6 py-3 flex flex-wrap items-center gap-3">
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                data-testid="doc-title"
                                className="flex-1 min-w-[200px] text-lg font-medium tracking-tight text-gray-900 bg-transparent outline-none"
                            />
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                data-testid="doc-status"
                                className="rounded-md border border-[#e8e8e3] bg-white px-2 h-8 text-xs capitalize"
                            >
                                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="doc-save"
                                className="inline-flex items-center gap-1 rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:opacity-50"
                            >
                                <Save className="w-3.5 h-3.5" strokeWidth={2} /> {saving ? "Saving…" : "Save"}
                            </button>
                            <button
                                onClick={exportPdf}
                                data-testid="doc-export-pdf"
                                className="inline-flex items-center gap-1 rounded-md border border-[#e8e8e3] px-3 h-8 text-xs font-medium text-gray-800 hover:border-[#d6d6cf]"
                            >
                                <Download className="w-3.5 h-3.5" strokeWidth={2} /> Export PDF
                            </button>
                            <button
                                onClick={() => del(active.id)}
                                title="Delete"
                                data-testid="doc-delete"
                                className="p-1.5 rounded-md text-gray-400 hover:text-red-600"
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                        </div>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            data-testid="doc-editor"
                            spellCheck
                            className="flex-1 min-h-0 px-6 py-4 font-mono text-[13px] leading-relaxed text-gray-800 bg-white outline-none resize-none"
                            placeholder="Start typing markdown or use AI Assist to draft…"
                        />
                    </>
                ) : (
                    <div className="flex-1 grid place-items-center text-sm text-gray-500">
                        Pick a document from the left, or create a new one.
                    </div>
                )}
            </main>

            {/* RIGHT rail — AI Assist */}
            {active && (
                <aside className="w-[340px] flex-shrink-0 border-l border-[#e8e8e3] bg-[#fafaf7] flex flex-col overflow-y-auto wfos-scrollbar p-5 gap-4">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#16a34a]" strokeWidth={1.75} />
                        <div className="wfos-label">AI Assist</div>
                    </div>
                    <div className="wfos-card p-3">
                        <label className="wfos-label">Draft</label>
                        <textarea
                            value={aiPrompt}
                            onChange={(e) => setAiPrompt(e.target.value)}
                            data-testid="doc-ai-prompt"
                            placeholder="Describe what you want. E.g. 'A 3-page proposal for a $180K CRM migration project.'"
                            className="mt-1.5 w-full h-24 rounded-md border border-[#e8e8e3] bg-white px-2 py-1.5 text-sm resize-none focus:outline-none focus:border-[#16a34a]"
                        />
                        <button
                            onClick={aiDraft}
                            disabled={aiStreaming || !aiPrompt.trim()}
                            data-testid="doc-ai-generate"
                            className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:opacity-50"
                        >
                            {aiStreaming ? "Streaming…" : "Generate"}
                        </button>
                    </div>
                    <div className="text-[10px] text-gray-400 leading-relaxed">
                        Powered by Claude Sonnet 4.5. Drafts stream directly into the editor and auto-save.
                    </div>
                    <div className="wfos-card p-3">
                        <div className="flex items-center gap-2 mb-2">
                            <History className="w-3.5 h-3.5 text-gray-400" strokeWidth={1.75} />
                            <div className="wfos-label">History</div>
                        </div>
                        <ul className="space-y-1 text-xs">
                            {versions.map((v) => (
                                <li key={v.id} className="flex items-center justify-between gap-2">
                                    <span className="text-gray-700">v{v.version}</span>
                                    <span className="text-gray-400">{new Date(v.changed_at).toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </aside>
            )}

            {/* Template picker modal */}
            {showTemplate && (
                <div className="fixed inset-0 bg-black/30 grid place-items-center z-50">
                    <div className="wfos-card p-6 w-[440px]" data-testid="doc-template-modal">
                        <div className="text-lg font-medium text-gray-900">New document</div>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="wfos-label">Title</label>
                                <input
                                    value={newTitle}
                                    onChange={(e) => setNewTitle(e.target.value)}
                                    data-testid="doc-new-title"
                                    autoFocus
                                    className="mt-1 w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="wfos-label">Template</label>
                                <select
                                    value={newTemplate}
                                    onChange={(e) => setNewTemplate(e.target.value)}
                                    data-testid="doc-new-template"
                                    className="mt-1 w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm"
                                >
                                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button onClick={() => setShowTemplate(false)} className="rounded-md border border-[#e8e8e3] px-3 h-8 text-xs text-gray-700">Cancel</button>
                            <button onClick={create} data-testid="doc-new-create" className="rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white">Create</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
